import {
  VendorFeedSpecs,
  V1VendorFeedSpecs,
  V2VendorFeedSpecs,
  isV1VendorFeedSpecs,
  isV2VendorFeedSpecs,
} from 'new/components/flows/types/elt/CreateELTFlow/types';

/**
 * Type-safe utility for accessing vendor feed specs properties
 * Handles both V1 and V2 template structures
 */

import { getDebugLogger } from 'new/hooks/useDebugLogger';

// Debug logging is now controlled by the UI_ENABLE_DEBUGGER feature flag
const debugLogger = getDebugLogger();

export class VendorFeedSpecsWrapper {
  private specs: VendorFeedSpecs;
  private templateVersion: 'v1' | 'v2';

  constructor(specs: VendorFeedSpecs, templateVersion: 'v1' | 'v2') {
    this.specs = specs;
    this.templateVersion = templateVersion;
  }

  /**
   * Get the template version
   */
  getTemplateVersion(): 'v1' | 'v2' {
    return this.templateVersion;
  }

  /**
   * Check if this is a V1 template
   */
  isV1Template(): boolean {
    return this.templateVersion === 'v1';
  }

  /**
   * Check if this is a V2 template
   */
  isV2Template(): boolean {
    return this.templateVersion === 'v2';
  }

  /**
   * Get display options (V1) or strategies (V2)
   */
  getDisplayOptions(): Record<string, { label: string; value: string }> {
    if (this.templateVersion === 'v1') {
      return this.specs.ref_feed_options.display;
    } else {
      return (this.specs as V2VendorFeedSpecs).ref_feed_options.strategies;
    }
  }

  /**
   * Get default reference feed (V1) or default strategy (V2)
   */
  getDefaultRef(): string | null {
    if (this.templateVersion === 'v1') {
      return this.specs.ref_feed_options.default_ref_feed;
    } else {
      return (this.specs as V2VendorFeedSpecs).ref_feed_options.default_ref_feed;
    }
  }

  /**
   * Get default include feeds (V1) or default include strategies (V2)
   */
  getDefaultInclude(): string[] {
    if (this.templateVersion === 'v1') {
      return this.specs.ref_feed_options.default_include_feeds;
    } else {
      // V2 templates use default_include_feeds (same as V1 for compatibility)
      return this.specs.ref_feed_options.default_include_feeds;
    }
  }

  /**
   * Get object detection mode selector parameter
   */
  getObjectDetectionModeParam(): string | undefined {
    if (!this.specs) {
      return undefined;
    }

    if (this.isV1Template()) {
      return this.specs.ref_feed_options?.object_detection_mode_selector_param;
    } else {
      const v2Specs = this.specs as V2VendorFeedSpecs;
      const objectDetectionCapability = v2Specs.capabilities?.OBJECT_DETECTION;
      return objectDetectionCapability?.param_name;
    }
  }

  /**
   * Get single schema parameter
   */
  getSingleSchemaParam(): string | undefined {
    if (!this.specs) {
      return undefined;
    }

    if (this.isV1Template()) {
      return this.specs.ref_feed_options?.single_schema_param;
    } else {
      const v2Specs = this.specs as V2VendorFeedSpecs;
      const multiSchemaCapability = v2Specs.capabilities?.MULTI_SCHEMA;
      return multiSchemaCapability?.param_name;
    }
  }

  /**
   * Get force resync support information
   */
  getForceResyncSupport(): Record<string, boolean> | undefined {
    if (!this.specs) {
      return undefined;
    }

    if (this.isV1Template()) {
      return this.specs.ref_feed_options?.supports_force_resync;
    } else {
      // For V2 templates, force resync support is still in ref_feed_options
      // as it's not a capability but a feed-level feature
      const v2Specs = this.specs as V2VendorFeedSpecs;
      return v2Specs.ref_feed_options?.supports_force_resync;
    }
  }

  /**
   * Check if a specific ref_feed supports force resync
   */
  supportsForceResync(refFeed: string): boolean {
    const forceResyncSupport = this.getForceResyncSupport();
    return forceResyncSupport?.[refFeed] === true;
  }

  /**
   * Get data feed options for a specific feed
   */
  getDataFeedOptions(feedKey: string) {
    return this.specs.data_feed_options[feedKey];
  }

  /**
   * Get primary key for a feed
   */
  getPrimaryKey(feedKey: string): string {
    const feedOptions = this.specs.data_feed_options[feedKey];
    debugLogger.debugLog(2, 'feedOptions', feedOptions);
    if (!feedOptions) return '';

    if (this.templateVersion === 'v1') {
      return (feedOptions as any).primary_key_selectors?.primary_key || '';
    } else {
      return Array.isArray((feedOptions as any).primary_keys)
        ? (feedOptions as any).primary_keys[0]
        : '';
    }
  }

  /**
   * Get reference feed selectors for a feed
   */
  getRefFeedSelectors(feedKey: string): string[] {
    const feedOptions = this.specs.data_feed_options[feedKey];
    if (!feedOptions) return [];

    return feedOptions.ref_feed_selectors || [];
  }

  /**
   * Get allowed strategies for a feed (V2 only)
   */
  getAllowedStrategies(feedKey: string): string[] {
    if (!this.isV2Template()) return [];

    const feedOptions = this.specs.data_feed_options[feedKey];
    if (!feedOptions) return [];

    return (feedOptions as any).allowed_strategies || [];
  }

  /**
   * Get feed parameters for a feed (V2 only)
   */
  getFeedParams(feedKey: string): any {
    if (!this.isV2Template()) return {};

    const feedOptions = this.specs.data_feed_options[feedKey];
    if (!feedOptions) return {};

    return (feedOptions as any).feed_params || {};
  }

  /**
   * Get strategy mapping for a feed (V2 only)
   */
  getStrategyMapping(feedKey: string): any {
    if (!this.isV2Template()) return {};

    const feedOptions = this.specs.data_feed_options[feedKey];
    if (!feedOptions) return {};

    return (feedOptions as any).strategy_mapping || {};
  }

  /**
   * Get strategy mappings (V2 only)
   */
  getStrategyMappings(): Record<string, string[]> {
    if (this.templateVersion === 'v2') {
      const mappings = (this.specs as V2VendorFeedSpecs).strategyMappings || {};
      // Convert the V2 strategy mappings format to the expected format
      const result: Record<string, string[]> = {};
      Object.entries(mappings).forEach(([strategy, mapping]) => {
        result[strategy] = mapping.ref_feeds || [];
      });
      return result;
    }
    return {};
  }

  /**
   * Get the raw specs object
   */
  getRawSpecs(): VendorFeedSpecs {
    return this.specs;
  }

  /**
   * Get display label for a reference feed or strategy
   */
  getDisplayLabel(key: string): string {
    const displayOptions = this.getDisplayOptions();
    return displayOptions[key]?.label || key;
  }

  /**
   * Get all available reference feeds or strategies
   */
  getAllDisplayKeys(): string[] {
    const displayOptions = this.getDisplayOptions();
    return Object.keys(displayOptions);
  }

  /**
   * Get ref field list selector parameter name
   */
  getRefFieldListSelector(): string {
    if (this.isV1Template()) {
      return (this.specs as V1VendorFeedSpecs).ref_feed_options?.ref_field_list_selector || '';
    } else {
      // For V2, extract from FIELD_SELECTION capability
      const v2Specs = this.specs as V2VendorFeedSpecs;
      const fieldSelectionCapability = v2Specs.capabilities?.FIELD_SELECTION;
      if (fieldSelectionCapability?.enabled && fieldSelectionCapability.params) {
        const fieldListSelectorParam = fieldSelectionCapability.params.find(
          (param: any) => param.config?.param_type === 'field_list_selector',
        );
        return fieldListSelectorParam?.name || '';
      }
      return '';
    }
  }

  /**
   * Get ref fields names parameter name
   */
  getRefFieldsNames(): string {
    if (this.isV1Template()) {
      return (this.specs as V1VendorFeedSpecs).ref_feed_options?.ref_fields_names || '';
    } else {
      // For V2, extract from FIELD_SELECTION capability
      const v2Specs = this.specs as V2VendorFeedSpecs;
      const fieldSelectionCapability = v2Specs.capabilities?.FIELD_SELECTION;
      if (fieldSelectionCapability?.enabled && fieldSelectionCapability.params) {
        const fieldListParam = fieldSelectionCapability.params.find(
          (param: any) => param.config?.param_type === 'field_list',
        );
        return fieldListParam?.name || '';
      }
      return '';
    }
  }

  /**
   * Get ref spec resource name for a given ref_spec_name
   */
  getRefSpecResourceName(refSpecName: string): string {
    if (this.isV1Template()) {
      const v1Specs = this.specs as V1VendorFeedSpecs;
      return v1Specs.ref_feed_options?.feed_names?.[refSpecName]?.param || '';
    } else {
      // For V2, this might be derived from strategyMappings or other logic
      return '';
    }
  }
}

/**
 * Factory function to create a VendorFeedSpecsWrapper
 */
export function createVendorFeedSpecsWrapper(
  specs: VendorFeedSpecs,
  templateVersion: 'v1' | 'v2',
): VendorFeedSpecsWrapper {
  return new VendorFeedSpecsWrapper(specs, templateVersion);
}

/**
 * Utility function to safely access vendor feed specs properties
 * @deprecated Use VendorFeedSpecsWrapper instead
 */
export function safeGetVendorFeedSpecsProperty<T>(
  specs: VendorFeedSpecs,
  propertyPath: string,
  defaultValue: T,
): T {
  try {
    const value = propertyPath.split('.').reduce((obj, key) => obj?.[key], specs as any);
    return value !== undefined ? value : defaultValue;
  } catch {
    return defaultValue;
  }
}
